import React, { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Grid,
  Button,
  Typography,
  Stack,
  Tooltip,
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CertificateCard from '../../SystemDesign/CertificateCard';
import useUploadEvaluationEvidences from '../../hooks/useUploadEvaluationEvidences';

/**
 * MyCerteficats
 * Props:
 * - evaluationId: string|number (required to enable upload)
 * - skillId?: number (optional, used when uploading, and to filter list)
 * - allowUpload?: boolean (default true)
 */
export default function MyCerteficats({ evaluationId, skillId, allowUpload = true }) {
  const fileInputRef = useRef(null);
  const { uploadForSkill } = useUploadEvaluationEvidences();
  const [uploading, setUploading] = useState(false);

  // Read evidences from Redux
  const raw = useSelector((s) => s.practicalEvidences);
  const evidences = Array.isArray(raw) ? raw : (raw?.items || []);

  // Filter by evaluation and optional skill
  const certificates = useMemo(() => {
    const byEval = evidences.filter((e) => {
      const evId = e.evaluationId ?? e.evaluation_id;
      return evaluationId ? String(evId) === String(evaluationId) : true;
    });
    const bySkill = skillId != null
      ? byEval.filter((e) => {
          const sk = e?.metadata?.skill_id ?? e?.metadata?.skillId;
          return Number(sk) === Number(skillId);
        })
      : byEval;

    // Map evidence -> CertificateCard props
    return bySkill.map((e) => {
      const url = e.url ?? e.evidence_url;
      // evidenceType is 'image' | 'video' | 'document' (no mime available from reducer)
      const type = e.evidenceType ?? e.evidence_type ?? 'document';
      const isImage = type === 'image';
      return {
        id: e.id,
        name: e.file_name || e.original_name || e.client_name || 'Untitled certificate',
        type, // could be a MIME when available; falls back to semantic type
        sizeBytes: e.sizeBytes, // often not provided
        createdAt: e.createdAt,
        updatedAt: e.updatedAt,
        status: e.status,
        fileUrl: url,
        thumbnailUrl: isImage ? url : undefined,
      };
    });
  }, [evidences, evaluationId, skillId]);

  const onClickUpload = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const onFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !evaluationId) return;
    try {
      setUploading(true);
      await uploadForSkill({
        evaluationId,
        skillId: skillId ?? null,
        files,
        level: null,
        score: null,
        comment: '',
      });
      // Clear input so the same file can be selected again
      e.target.value = '';
    } finally {
      setUploading(false);
    }
  };

  const canUpload = allowUpload && Boolean(evaluationId);

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6">Certificates</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {certificates.length} item{certificates.length === 1 ? '' : 's'}
          </Typography>
          <Tooltip title={canUpload ? 'Upload certificates' : 'Provide an evaluation to enable upload'}>
            <span>
              <Button
                variant="contained"
                size="small"
                startIcon={<CloudUploadOutlinedIcon />}
                disabled={!canUpload || uploading}
                onClick={onClickUpload}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </span>
          </Tooltip>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={onFilesSelected}
            accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          />
        </Stack>
      </Stack>

      {certificates.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No certificates yet.</Typography>
      ) : (
        <Grid container spacing={2}>
          {certificates.map((c) => (
            <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={c.id || c.fileUrl || c.name}>
              <CertificateCard certificate={c} onClick={() => c.fileUrl && window.open(c.fileUrl, '_blank')} />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}